import { Box, FormControl, FormHelperText, InputLabel, ListSubheader, MenuItem, SelectChangeEvent, Stack, Typography } from "@mui/material";
import React, { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { SideSheetFieldPropsBase } from "../side-sheet/SideSheet";
import DefaultSelect from "../../default-select/DefaultSelect";

export type SideSheetGroupedSelectOption<T extends string | number = number> = {
    label: string,
    value: T,
    isDefault?: boolean;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
    disabled?: boolean;
    createdUserName?: string;
};

export type SideSheetGroupedSelectOptionGroup<T extends string | number = number> = {
    heading: string;
    options: Array<SideSheetGroupedSelectOption<T>>;
};

export type SideSheetGroupedSelectProps<T extends string | number = number> = {
    placeholder?: string | null;
    placeholderValue?: T;
    placeholderSelectEnabled?: boolean;
    placeholderIsDefault?: boolean;
    optionGroups: Array<SideSheetGroupedSelectOptionGroup<T>>;
    value: T | undefined;
    onChange?: (value: T) => void;
    size?: "small" | "medium";
    hideLabel?: boolean;
} & SideSheetFieldPropsBase;

export default function SideSheetGroupedSelect<T extends string | number = number>({
    id,
    testId,
    label,
    ariaLabel,
    readOnly,
    helperText,
    sx,
    disabled,
    placeholder,
    placeholderValue = -1 as T,
    placeholderSelectEnabled = false,
    placeholderIsDefault = false,
    value,
    optionGroups,
    onChange,
    size,
    hideLabel
}: SideSheetGroupedSelectProps<T>) {

    const adjustedValue =
        typeof value === "string"
            ? value as string
            : Number.isFinite(value)
                ? (value === -1 && !placeholder)
                    ? ""
                    : value as number
                : placeholder
                    ? -1
                    : "";

    return (
        <FormControl
            aria-label={ariaLabel}
            variant="filled"
            sx={{
                marginTop: 0,
                ...sx
            }}>
            {!hideLabel && <InputLabel shrink={true} >{label}</InputLabel>}
            <DefaultSelect<T>
                size={size}
                id={id}
                data-testid={testId}
                readOnly={readOnly}
                value={adjustedValue as T}
                disabled={disabled}
                onChange={(event: SelectChangeEvent<T>) => {
                    const value = event.target.value;
                    onChange?.(value as T);
                }}
            >
                {placeholder &&
                    <MenuItem
                        key={placeholderValue}
                        value={placeholderValue}
                        disabled={!placeholderSelectEnabled}
                    >
                        {placeholder}{placeholderIsDefault && <Default />}
                    </MenuItem>
                }

                {optionGroups?.map((group, groupIndex) => [
                    <ListSubheader key={`g${groupIndex}`}>
                        <OptionGroupHeading name={group.heading} />
                    </ListSubheader>,

                    group.options?.map((configuration, configurationIndex) =>
                        <MenuItem key={`g${groupIndex}c${configurationIndex}`} 
                            value={configuration.value}
                            disabled={configuration.disabled}
                            >
                            <OptionName
                                name={configuration.label}
                                isDefault={configuration.isDefault}
                                startAdornment={configuration.startAdornment}
                                endAdornment={configuration.endAdornment}
                            />
                        </MenuItem>
                    )
                ])}
            </DefaultSelect>
            {helperText &&
                <FormHelperText>{helperText}</FormHelperText>
            }
        </FormControl>
    );
}

function Default() {
    const { t } = useTranslation();

    return (
        <Typography variant="overline" sx={{
            marginLeft: 2,
            opacity: "0.5"
        }}>
            {t("configurationList.default")}
        </Typography>
    );
}

interface OptionGroupHeadingProps {
    name?: string;
}

function OptionGroupHeading({ name }: OptionGroupHeadingProps) {
    return (
        <Box component="span" sx={{
            fontSize: "1rem",
            textTransform: "uppercase",
            display: "block",
        }}>
            {name}
        </Box>
    );
}

interface OptionNameProps {
    name?: string;
    isDefault?: boolean;
    startAdornment?: ReactNode;
    endAdornment?: ReactNode;
}

function OptionName({
    name,
    isDefault,
    startAdornment,
    endAdornment,

}: OptionNameProps) {
    return (
        <Box
            sx={{
                paddingLeft: 2,
                position: "relative",
                flex: "1 1 auto",
                display: "flex",
                alignItems: "center",
            }}
        >
            <Stack direction="row" sx={{ flex: "1 1 auto", alignItems: "center" }} spacing={1}>
                {startAdornment && <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>{startAdornment}</Box>}
                <Box sx={{ flex: "1 1 auto", display: "flex", alignItems: "center" }}>{name}{isDefault && <Default />}</Box>
                {endAdornment && <Box sx={{ flex: "0 0 auto", display: "flex", alignItems: "center" }}>{endAdornment}</Box>}
            </Stack>
        </Box>
    );
}